import React, { useState, ChangeEvent, useEffect, ReactElement, useContext } from 'react';

import clsx from 'clsx';
import { useDetectClickOutside } from 'react-detect-click-outside';
import { Control } from 'react-hook-form';
import { FieldErrors } from 'react-hook-form/dist/types';
import { MultiValue, SingleValue } from 'react-select';
import { Suggestion, getGeocode } from 'use-places-autocomplete';

import Input from 'components/Inputs/Input';
import Select from 'components/Select';
import states from 'enums/StatesList';
import { GooglePlacesContext } from 'index';
import { getAddressParts } from 'utils/address';
import { ZIP_CODE_MAX_LENGTH } from 'utils/constants';
import { formatOptionLabel, OptionType } from 'utils/inputSelect';

import styles from './SearchLocationInput.module.scss';

export interface AddressType {
  streetAddress: string;
  city: string;
  state: string;
  zip: string;
  county?: string;
}

interface SearchLocationInputProps {
  getValues: (name: string) => string;
  streetLabel?: string;
  cityLabel?: string;
  stateLabel?: string;
  zipLabel?: string;
  errors?: FieldErrors<AddressType>;
  inputChange?: (name: string, value: string) => void;
  onChange?: (event: AddressType) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  initialValue?: AddressType;
  inputRegister?: React.Ref<HTMLInputElement>;
  inputNames?: AddressType;
  control?: Control<any>;
  useFormControl?: boolean;
  required?: boolean;
  loadScript?: boolean;
  caption?: string;
  containerClassName?: string;
  cityInputClassName?: string;
  otherAddressInputsClassName?: string;
  alternativeRowClassName?: string;
  locationInputClassName?: string;
  captionClassName?: string;
  disabled?: boolean;
  onStateChange?: () => void;
}

export interface GeoCompletionResult {
  description: string;
}

enum InputLabels {
  StreetAddress = 'Street Address',
  City = 'City',
  State = 'State',
  ZipCode = 'Zip Code',
}

const ENTER = 13;
const ESCAPE = 27;
const UP_ARROW = 38;
const DOWN_ARROW = 40;
const STATUS_OK = 'OK';
const SHORT_STATE_KEY_LENGTH = 2;
const CITY_MAX_LENGTH = 100;

const cachedVal = '';
const acceptedKeys = [ENTER, ESCAPE, UP_ARROW, DOWN_ARROW];

const options = [
  ...states.map(({ value }) => ({
    value: value,
    label: value,
  })),
];

const statesArray = states.map(({ value }) => value);

const SearchLocationInputNew = ({
  streetLabel,
  cityLabel,
  stateLabel,
  zipLabel,
  onChange,
  inputChange,
  initialValue,
  onBlur,
  getValues,
  inputRegister,
  errors,
  control,
  useFormControl,
  required,
  inputNames = { streetAddress: 'street_address', city: 'city', state: 'state', zip: 'zip', county: 'county' },
  caption,
  containerClassName,
  cityInputClassName,
  otherAddressInputsClassName,
  alternativeRowClassName,
  locationInputClassName,
  captionClassName,
  disabled,
  onStateChange,
}: SearchLocationInputProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const {
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = useContext(GooglePlacesContext)!;

  const [address, setAddress] = useState<AddressType>({
    streetAddress: '',
    city: '',
    state: '',
    zip: '',
    county: '',
  });

  const hasSuggestions = status === STATUS_OK;

  const [currIndex, setCurrIndex] = useState<number | null>(null);

  const dismissSuggestions = (): void => {
    setCurrIndex(null);
    clearSuggestions();
  };

  const ref = useDetectClickOutside({
    onTriggered: () => {
      setIsFocused(false);

      dismissSuggestions();
    },
  });

  const handleInput = (e: ChangeEvent<HTMLInputElement>): void => {
    setIsFocused(true);

    const { name, value } = e.target;

    if (e.target.name === inputNames.streetAddress) {
      setValue(e.target.value);
    }

    setAddress({ ...address, [e.target.name]: e.target.value });

    if (inputChange) {
      inputChange(name, value);
    }

    if (onChange) {
      onChange({ ...address, [e.target.name]: e.target.value });
    }
  };

  const handleState = (option: MultiValue<OptionType> | SingleValue<OptionType>) => {
    if (
      !option ||
      (option as OptionType).value.length > SHORT_STATE_KEY_LENGTH ||
      !statesArray.includes((option as OptionType).value)
    ) {
      return;
    }

    setAddress({ ...address, [inputNames?.state]: (option as OptionType).value });

    if (onChange) {
      onChange({ ...address, [inputNames?.state]: (option as OptionType).value });
    }

    if (onStateChange) {
      onStateChange();
    }
  };

  const handleEnter = (idx: number) => (): void => {
    setCurrIndex(idx);
  };

  const handleLeave = (): void => {
    setCurrIndex(null);
  };

  const handleSelect = async (event: GeoCompletionResult) => {
    const { description } = event;

    const [addressData] = await getGeocode({ address: description });
    const parts = getAddressParts(addressData.address_components);

    setValue(description.split(',')[0], false);

    setAddress({
      streetAddress: description.split(',')[0],
      city: parts.city,
      state: parts.state,
      zip: parts.zip,
      county: parts.county,
    });

    clearSuggestions();

    if (inputChange) {
      inputChange(inputNames?.streetAddress, description.split(',')[0]);
      inputChange(inputNames?.city, parts.city);
      inputChange(inputNames?.state, parts.state);
      inputChange(inputNames?.zip, parts.zip);

      if (inputNames?.county) {
        inputChange(inputNames?.county ?? '', parts.county);
      }
    }

    if (onChange) {
      onChange({
        streetAddress: description.split(',')[0],
        city: parts.city,
        state: parts.state,
        zip: parts.zip,
        county: parts.county,
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (!hasSuggestions || !acceptedKeys.includes(e.keyCode)) return;

    if (e.keyCode === ESCAPE) {
      dismissSuggestions();

      return;
    }

    if (e.keyCode === ENTER) {
      dismissSuggestions();

      if (data[currIndex!]) {
        handleSelect(data[currIndex!]);
      }

      return;
    }

    let nextIndex: number | null;

    if (e.keyCode === UP_ARROW) {
      e.preventDefault();

      nextIndex = currIndex ?? data.length;
      nextIndex = nextIndex > 0 ? nextIndex - 1 : null;
    } else {
      nextIndex = currIndex ?? -1;
      nextIndex = nextIndex < data.length - 1 ? nextIndex + 1 : null;
    }

    setCurrIndex(nextIndex);
    setValue(data[nextIndex!] ? data[nextIndex!].description : cachedVal, false);
  };

  useEffect(() => {
    if (initialValue) {
      setAddress(initialValue);
    }
  }, [initialValue]);

  const renderSuggestions = () =>
    data.map((suggestion: Suggestion, index: number) => {
      const {
        structured_formatting: { main_text, secondary_text }, // eslint-disable-line
      } = suggestion;

      return (
        <li
          key={`ex-list-item-${index}`}
          id={`ex-list-item-${index}`}
          className={index === currIndex ? styles.listItemActive : styles.listItem}
          onClick={() => handleSelect(suggestion)}
          onMouseEnter={handleEnter(index)}
          role="option"
          aria-selected={index === currIndex}
        >
          <span className={clsx('button_label', styles.textBold)}>{main_text} </span>
          <span className={clsx('button_label', styles.textLight)}>{secondary_text}</span>
        </li>
      );
    });

  const handleOnFocus = () => {
    setIsFocused(true);
  };

  return (
    <div className={clsx(styles.searchLocationInput, containerClassName)}>
      <div ref={ref} aria-owns="ex-list-box" aria-haspopup="listbox" aria-expanded={hasSuggestions}>
        <div className={styles.streetAddressInput}>
          <Input
            inputRef={inputRegister}
            label={streetLabel || InputLabels.StreetAddress}
            name={inputNames?.streetAddress}
            value={address.streetAddress}
            onChange={handleInput}
            inputContainerClassName={styles.streetAddressInput}
            inputClassName={hasSuggestions ? styles.inputWithDropdown : styles.input}
            onKeyDown={handleKeyDown}
            errorMessage={errors?.streetAddress?.message}
            onFocus={handleOnFocus}
            onBlur={onBlur}
            autoComplete="off"
            caption={caption}
            captionClassName={captionClassName}
            disabled={disabled}
          />
          {isFocused && hasSuggestions && (
            <ul id="ex-list-box" onMouseLeave={handleLeave} role="listbox" className={styles.list}>
              {renderSuggestions()}
            </ul>
          )}
        </div>
      </div>
      <div className={clsx(styles.cityStateZipRow, alternativeRowClassName, locationInputClassName)}>
        <Input
          inputRef={inputRegister}
          label={cityLabel || InputLabels.City}
          name={inputNames?.city}
          onChange={handleInput}
          inputContainerClassName={cityInputClassName}
          value={address.city}
          errorMessage={errors?.city?.message}
          onBlur={onBlur}
          maxLength={CITY_MAX_LENGTH}
          disabled={disabled}
        />
        <div className={clsx(styles.stateZipSSNRow, alternativeRowClassName)}>
          <div className={clsx(styles.stateZipRow, alternativeRowClassName)}>
            <Select
              useFormControl={useFormControl}
              required={required}
              control={control}
              inputContainerClassName={clsx(styles.otherAddressInputs, otherAddressInputsClassName)}
              className={clsx(styles.otherAddressInputs, otherAddressInputsClassName)}
              value={options.find((obj) => obj.value === address.state)}
              label={stateLabel || InputLabels.State}
              name={inputNames?.state}
              onChange={handleState}
              options={options}
              isSearchable
              errorMessage={errors?.state?.message}
              placeholder=""
              formatOptionLabel={formatOptionLabel}
              onInputChange={(value) => {
                if (value) {
                  handleState({ value: value.toUpperCase(), label: value.toUpperCase() });

                  return value.length <= SHORT_STATE_KEY_LENGTH
                    ? value.replace(/[^A-Z,a-z]/g, '').slice(0, SHORT_STATE_KEY_LENGTH)
                    : '';
                }
              }}
              onBlur={() => {
                if (!options.some((option) => option.value === getValues(inputNames?.state))) {
                  handleState({ value: '', label: '' });
                }
              }}
              isClearable
              isDisabled={disabled}
              noInteraction={disabled}
            />
            <Input
              inputRef={inputRegister}
              label={zipLabel || InputLabels.ZipCode}
              name={inputNames?.zip}
              onChange={handleInput}
              inputContainerClassName={clsx(styles.otherAddressInputs, otherAddressInputsClassName)}
              value={address.zip?.trim().replace(/\D/g, '')}
              errorMessage={errors?.zip?.message}
              maxLength={ZIP_CODE_MAX_LENGTH}
              onBlur={onBlur}
              disabled={disabled}
              inputMode="numeric"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchLocationInputNew;
